import { Globe, Pencil, Plus, Power, PowerOff, RefreshCw, Trash2, Wifi } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../../../core/components/Button';
import { Card } from '../../../core/components/Card';
import { XuiHostRequest, XuiServerConfig } from '../../../core/models/xui';
import { useXuiServerActions } from '../hooks/useXuiServerActions';
import { AddXuiClientsDialog } from './AddXuiClientsDialog';
import { AddXuiHostDialog } from './AddXuiHostDialog';
import { XuiServersExportDialog } from './XuiServersExportDialog';
import './XuiServersRegistryPanel.css';

interface XuiServersRegistryPanelProps {
    servers: XuiServerConfig[];
    onServersChange?: () => void;
}

export const XuiServersRegistryPanel: React.FC<XuiServersRegistryPanelProps> = ({ servers, onServersChange }) => {
    const [revealedPasswords, setRevealedPasswords] = useState<Record<string, boolean>>({});
    const [showAddClientsDialog, setShowAddClientsDialog] = useState<string | null>(null);
    const [showAddHostDialog, setShowAddHostDialog] = useState(false);
    const [editingHost, setEditingHost] = useState<XuiServerConfig | null>(null);
    const [showServersExport, setShowServersExport] = useState(false);

    const {
        loadingStates,
        savingHost,
        latencies,
        supportedTypes,
        handleActivate,
        handleDeactivate,
        handleDelete,
        handleAddClientsConfirm,
        handleSyncAllClients,
        handleUpdateGeofile,
        handleMeasureLatency,
        handleAddHost,
        handleUpdateHost,
    } = useXuiServerActions(servers, onServersChange);

    const togglePasswordVisibility = (identity: string) => {
        setRevealedPasswords(prev => ({ ...prev, [identity]: !prev[identity] }));
    };
    
    
    if (showServersExport) {
        return <XuiServersExportDialog servers={servers} onClose={() => setShowServersExport(false)} />
    }

    return (
        <Card className="sync-actions-panel">

            <div className="xui-servers-header">
                <h3>Зарегистрированные XUI серверы</h3>
                <Button variant="secondary" onClick={() => setShowServersExport(true)} disabled={servers.length === 0}>
                    Экспорт серверов (CSV)
                </Button>
                <Button
                    variant="secondary"
                    icon={<Plus size={16} />}
                    onClick={() => setShowAddHostDialog(true)}
                    title="Добавить новый XUI хост"
                >
                    Добавить хост
                </Button>
            </div>
            <div className="xui-server-list">
                {servers.length === 0 ? (
                    <div className="xui-empty">Нет зарегистрированных хостов</div>
                ) : (
                    servers.map(server => {
                        const isRevealed = Boolean(revealedPasswords[server.identity]);
                        const loading = loadingStates[server.identity] || {};
                        const isActive = server.active === true;
                        const latency = latencies[server.identity];
                        const latencyValue = latency?.success ? latency.result : null;

                        return (
                            <div key={server.identity} className={`xui-server-card ${isActive ? 'active' : 'inactive'}`}>
                                <div className="xui-server-left">
                                    <div className="xui-server-name">
                                        <b>{server.frendlyname}</b>
                                        <span className="xui-identity">({server.identity})</span>
                                        <span className={`xui-status ${isActive ? 'active' : ''}`}>
                                            {isActive ? 'активен' : 'неактивен'}
                                        </span>
                                        {latencyValue !== null && (
                                            <span className={`xui-latency ${latencyValue < 100 ? 'good' : latencyValue < 300 ? 'medium' : 'bad'}`}>
                                                <Wifi size={12} /> {latencyValue}мс
                                            </span>
                                        )}
                                        {latency && !latency.success && (
                                            <span className="xui-latency unavailable">
                                                <Wifi size={12} /> N/A
                                            </span>
                                        )}
                                    </div>
                                    <div className="xui-server-details">
                                        <div>host: {server.host}</div>
                                        {server.token ? (
                                            <div>
                                                token:{' '}
                                                <span
                                                    className={`server-password ${isRevealed ? 'revealed' : ''}`}
                                                    onClick={() => togglePasswordVisibility(server.identity)}
                                                    title={isRevealed ? 'Скрыть token' : 'Показать token'}
                                                >
                                                    {server.token}
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div>user: {server.user}</div>
                                                <div>
                                                    password:{' '}
                                                    <span
                                                        className={`server-password ${isRevealed ? 'revealed' : ''}`}
                                                        onClick={() => togglePasswordVisibility(server.identity)}
                                                        title={isRevealed ? 'Скрыть пароль' : 'Показать пароль'}
                                                    >
                                                        {server.password}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="xui-server-right">
                                    {isActive ? (
                                        <button
                                            className="xui-icon-btn deactivate"
                                            onClick={() => handleDeactivate(server)}
                                            disabled={loading.deactivate}
                                            title="Деактивировать хост"
                                        >
                                            {loading.deactivate ? <RefreshCw size={16} className="spin" /> : <PowerOff size={16} />}
                                        </button>
                                    ) : (
                                        <button
                                            className="xui-icon-btn activate"
                                            onClick={() => handleActivate(server)}
                                            disabled={loading.activate}
                                            title="Активировать хост"
                                        >
                                            {loading.activate ? <RefreshCw size={16} className="spin" /> : <Power size={16} />}
                                        </button>
                                    )}
                                    <button
                                        className="xui-icon-btn"
                                        onClick={() => setShowAddClientsDialog(server.identity)}
                                        disabled={loading.add}
                                        title="Добавить пользователей вручную"
                                    >
                                        {loading.add ? <RefreshCw size={16} className="spin" /> : <Plus size={16} />}
                                    </button>
                                    <button
                                        className="xui-icon-btn"
                                        onClick={() => handleSyncAllClients(server)}
                                        disabled={loading.sync}
                                        title="Синхронизировать пользователей с XUI сервером"
                                    >
                                        {loading.sync ? <RefreshCw size={16} className="spin" /> : <RefreshCw size={16} />}
                                    </button>
                                    <button
                                        className="xui-icon-btn"
                                        onClick={() => handleUpdateGeofile(server)}
                                        disabled={loading.geo}
                                        title="Обновить geo-файлы стран на XUI сервере"
                                    >
                                        {loading.geo ? <RefreshCw size={16} className="spin" /> : <Globe size={16} />}
                                    </button>
                                    <button
                                        className="xui-icon-btn"
                                        onClick={() => handleMeasureLatency(server)}
                                        disabled={loading.latency}
                                        title="Измерить задержку до сервера"
                                    >
                                        {loading.latency ? <RefreshCw size={16} className="spin" /> : <Wifi size={16} />}
                                    </button>
                                    <button
                                        className="xui-icon-btn"
                                        onClick={() => setEditingHost(server)}
                                        disabled={loading.update}
                                        title="Редактировать хост"
                                    >
                                        {loading.update ? <RefreshCw size={16} className="spin" /> : <Pencil size={16} />}
                                    </button>
                                    <button
                                        className="xui-icon-btn delete"
                                        onClick={() => handleDelete(server)}
                                        disabled={loading.delete}
                                        title="Удалить хост"
                                    >
                                        {loading.delete ? <RefreshCw size={16} className="spin" /> : <Trash2 size={16} />}
                                    </button>
                                </div>

                                {showAddClientsDialog === server.identity && (
                                    <AddXuiClientsDialog
                                        onClose={() => setShowAddClientsDialog(null)}
                                        onConfirm={(clients) => handleAddClientsConfirm(clients, server)}
                                    />
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {showAddHostDialog && (
                <AddXuiHostDialog
                    onClose={() => setShowAddHostDialog(false)}
                    onConfirm={handleAddHost}
                    supportedTypes={supportedTypes}
                    isLoading={savingHost}
                />
            )}

            {editingHost && (
                <AddXuiHostDialog
                    onClose={() => setEditingHost(null)}
                    onConfirm={(hosts) => {
                        const host = Array.isArray(hosts) ? hosts[0] : hosts as XuiHostRequest;
                        handleUpdateHost(host, editingHost).then(() => setEditingHost(null));
                    }}
                    supportedTypes={supportedTypes}
                    isLoading={savingHost}
                    editMode={true}
                    initialData={editingHost}
                />
            )}
        </Card>
    );
};
